import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import videojs from "video.js";
import { useQuery } from "@apollo/client";
import { useRecoilState } from "recoil";
import { useHistory, useLocation } from "react-router-dom";
import { MdClose, MdDownload, MdMovieFilter } from "react-icons/md";

import { VideoPlayer } from "MVVM/VideoPlayer";
import { MatchVodViewerState } from "MVVM/State/matchVod";
import { useSubscriptionInfo } from "MVVM/Hooks/useHasProAccess";
import { MATCH_VOD_DETAIL } from "MVVM/GraphQL/matchHistory";
import { TMoment, TMatchVod } from "MVVM/Views/MatchHistory/@types";
import { getMapIconUrl } from "MVVM/Views/MatchHistory/utils";
import { FeedbackModal } from "MVVM/Components/FeedbackModal";
import { useMatchVodViewerModel, momentRound } from "./ViewModel";
import { MomentRow } from "./MomentRow";
import { MomentSummary } from "./MomentSummary";
import {
  Backdrop,
  Shell,
  LoadingShell,
  LoadingSpinner,
  LoadingText,
  Header,
  HeaderBg,
  HeaderScrim,
  HeaderTop,
  HeaderLeft,
  IdentityRow,
  MapIcon,
  EyebrowRow,
  Eyebrow,
  BetaTag,
  FeedbackWrap,
  MapName,
  HeaderRight,
  MetaRow,
  HeaderDesc,
  ResultBadge,
  Score,
  DownloadLink,
  CloseButton,
  Body,
  Stage,
  PlayerWrap,
  Seeker,
  Track,
  Fill,
  MomentBand,
  Divider,
  Segment,
  Playhead,
  RoundTip,
  TipMoment,
  TipTop,
  TipRound,
  TipTitle,
  TipBest,
  TipEmpty,
  FlashOverlay,
  FlashRound,
  FlashTitle,
  SeekerKey,
  KeyItem,
  KeyDot,
  KeyHint,
  Rail,
  RailInner,
  RailHeader,
  RailTitle,
  RailCount,
  RailSub,
  RailList,
  RailEmpty,
} from "./styledComponents";

const clamp = (n: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, n));

const fmtDate = (v?: string | number | null) => {
  if (!v) return "";
  const raw = typeof v === "string" && /^\d+$/.test(v) ? Number(v) : v;
  const d = new Date(raw as string | number);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const fmtDuration = (s?: number | null) => {
  if (!s) return "";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}m ${sec.toString().padStart(2, "0")}s`;
};

const resultMeta = (
  r?: string | null,
): { label: string; variant: "win" | "tie" | "loss" } => {
  if (["Win", "Victory"].includes(r || ""))
    return { label: "Victory", variant: "win" };
  if (r === "Tie") return { label: "Tie", variant: "tie" };
  return { label: "Defeat", variant: "loss" };
};

export const MatchVodViewer = () => {
  const [viewer, setViewer] = useRecoilState(MatchVodViewerState);
  const history = useHistory();
  const location = useLocation();
  const { isOpen, matchId, playerGameId } = viewer;

  const { data, loading: matchLoading } = useQuery(MATCH_VOD_DETAIL, {
    variables: { matchId, playerGameId },
    skip: !isOpen || !matchId || !playerGameId,
    fetchPolicy: "cache-and-network",
  });

  const match = data?.match;
  const vod: TMatchVod | undefined | null = match?.matchVod;
  const matchTitle = match?.title ?? null;
  const mapHero = match?.hero ?? null;

  const { isCreatorPlat, isInternal } = useSubscriptionInfo();
  const canDownload = isCreatorPlat || isInternal;

  const playerRef = useRef<videojs.Player | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const [currentSec, setCurrentSec] = useState(0);
  const [duration, setDuration] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [hoveredRound, setHoveredRound] = useState<number | null>(null);
  const [flash, setFlash] = useState<{
    id: number;
    round: number;
    text: string;
  } | null>(null);
  const flashId = useRef(0);
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollPause = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pauseAutoScroll = useCallback(() => {
    setAutoScroll(false);
    if (scrollPause.current) clearTimeout(scrollPause.current);
    scrollPause.current = setTimeout(() => setAutoScroll(true), 5000);
  }, []);

  const markers = vod?.roundMarkers || [];
  const totalMs = markers.length ? markers[markers.length - 1].endMs : 0;
  const domainSec = duration || totalMs / 1000 || 0;
  const currentMs = currentSec * 1000;
  const activeRound =
    markers.find((m) => currentMs >= m.startMs && currentMs < m.endMs)?.round ??
    null;

  const { moments, loading, markerByRound } = useMatchVodViewerModel(
    matchId,
    playerGameId,
    markers,
  );

  const close = useCallback(() => {
    setViewer({ isOpen: false });
    const params = new URLSearchParams(location.search);
    if (params.has("vod")) {
      params.delete("vod");
      const query = params.toString();
      history.replace(
        query ? `${location.pathname}?${query}` : location.pathname,
      );
    }
  }, [setViewer, history, location]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, close]);

  useEffect(() => {
    setCurrentSec(0);
    setDuration(0);
  }, [vod?.id]);

  const onReady = useCallback((player?: videojs.Player | null) => {
    if (!player) return;
    const sync = () => setCurrentSec(player.currentTime() || 0);
    const syncDur = () => setDuration(player.duration() || 0);
    player.on("timeupdate", sync);
    player.on("loadedmetadata", syncDur);
    player.on("durationchange", syncDur);
  }, []);

  const seekToSec = useCallback((sec: number) => {
    const p = playerRef.current;
    if (!p) return;
    p.currentTime(sec);
    setCurrentSec(sec);
  }, []);

  const seekFromClientX = useCallback(
    (clientX: number) => {
      const rect = barRef.current?.getBoundingClientRect();
      if (!rect || !domainSec) return;
      const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
      seekToSec(ratio * domainSec);
    },
    [domainSec, seekToSec],
  );

  useEffect(() => {
    if (!dragging) return;
    const move = (e: MouseEvent) => seekFromClientX(e.clientX);
    const up = () => setDragging(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [dragging, seekFromClientX]);

  const momentsByRound = useMemo(() => {
    const byRound = new Map<number, TMoment[]>();
    moments.forEach((m) => {
      const r = momentRound(m);
      if (r == null) return;
      if (!byRound.has(r)) byRound.set(r, []);
      byRound.get(r)!.push(m);
    });
    return byRound;
  }, [moments]);

  const momentSecs = useMemo(() => {
    const secs = new Map<string, number>();
    if (!domainSec) return secs;
    momentsByRound.forEach((ms, r) => {
      const marker = markerByRound.get(r);
      if (!marker) return;
      const startSec = marker.startMs / 1000;
      const endSec = marker.endMs / 1000;
      ms.forEach((m, i) => {
        const frac = (i + 1) / (ms.length + 1);
        secs.set(m.id, startSec + frac * (endSec - startSec));
      });
    });
    return secs;
  }, [momentsByRound, markerByRound, domainSec]);

  const jumpToMoment = useCallback(
    (moment: TMoment) => {
      const sec = momentSecs.get(moment.id);
      if (sec != null) {
        seekToSec(sec);
        return;
      }
      const marker = markerByRound.get(momentRound(moment) ?? -1);
      if (marker) seekToSec(marker.startMs / 1000);
    },
    [momentSecs, markerByRound, seekToSec],
  );

  useEffect(() => {
    if (activeRound == null) return;
    const ms = momentsByRound.get(activeRound);
    if (!ms || ms.length === 0) return;
    const id = flashId.current + 1;
    flashId.current = id;
    setFlash({ id, round: activeRound, text: ms[0].title });
    const t = setTimeout(
      () => setFlash((f) => (f?.id === id ? null : f)),
      4500,
    );
    return () => clearTimeout(t);
  }, [activeRound, momentsByRound]);

  if (!isOpen) return null;

  if (!vod?.url) {
    if (matchLoading) {
      return createPortal(
        <Backdrop onMouseDown={close}>
          <Shell onMouseDown={(e) => e.stopPropagation()}>
            <LoadingShell>
              <LoadingSpinner />
              <LoadingText>Loading match VOD...</LoadingText>
            </LoadingShell>
          </Shell>
        </Backdrop>,
        document.body,
      );
    }
    return null;
  }

  const playheadPct = domainSec
    ? clamp((currentSec / domainSec) * 100, 0, 100)
    : 0;
  const dividers = domainSec
    ? markers
        .slice(1)
        .map((m) => clamp((m.startMs / 1000 / domainSec) * 100, 0, 100))
    : [];
  const seg = (m: { startMs: number; endMs: number }) => ({
    left: (m.startMs / 1000 / domainSec) * 100,
    width: ((m.endMs - m.startMs) / 1000 / domainSec) * 100,
  });
  const hoveredMarker =
    hoveredRound != null
      ? markers.find((m) => m.round === hoveredRound) || null
      : null;

  const [scoreText, ...mapParts] = (matchTitle || "").split(" on ");
  const mapName = mapParts.join(" on ");
  const mapIcon = getMapIconUrl(mapHero || undefined);
  const result = resultMeta(match?.result);
  const date = fmtDate(match?.matchPlayedAt);
  const dur = fmtDuration(match?.durationInSeconds);
  const metaBits = [date, match?.gameMode, dur].filter(Boolean);
  const bestMoment = moments.length
    ? moments.reduce((a, b) => ((b.score ?? 0) > (a.score ?? 0) ? b : a))
    : null;
  const bestMomentId = bestMoment?.id;
  const bestRound = bestMoment ? momentRound(bestMoment) : null;

  return createPortal(
    <Backdrop onMouseDown={close}>
      <Shell onMouseDown={(e) => e.stopPropagation()}>
        <Header>
          <HeaderBg $bg={mapHero || undefined} />
          <HeaderScrim />
          <HeaderTop>
            <HeaderLeft>
              <EyebrowRow>
                <Eyebrow>
                  <MdMovieFilter size={19} />
                  Full Match VOD
                </Eyebrow>
                <BetaTag>Beta</BetaTag>
              </EyebrowRow>

              <IdentityRow>
                <ResultBadge $variant={result.variant}>
                  {result.label}
                </ResultBadge>
                {scoreText && mapName && (
                  <Score $variant={result.variant}>{scoreText}</Score>
                )}
                {mapIcon && <MapIcon src={mapIcon} alt="" />}
                <MapName>{mapName || matchTitle || "Match VOD"}</MapName>
              </IdentityRow>

              {metaBits.length > 0 && (
                <MetaRow>{metaBits.join("  ·  ")}</MetaRow>
              )}

              <HeaderDesc>
                Rewatch every round and clip your best moments in a single
                click.
              </HeaderDesc>
            </HeaderLeft>

            <HeaderRight>
              <FeedbackWrap>
                <FeedbackModal />
              </FeedbackWrap>
              {canDownload && (
                <DownloadLink
                  href={`${vod.url}?download`}
                  download
                  target="_blank"
                  rel="noreferrer"
                >
                  <MdDownload size={16} />
                  Download Video
                </DownloadLink>
              )}
            </HeaderRight>
          </HeaderTop>

          <CloseButton onClick={close} aria-label="Close">
            <MdClose size={18} />
          </CloseButton>
        </Header>

        <Body>
          <Stage>
            <PlayerWrap>
              <VideoPlayer
                key={vod.id}
                mp4Source={vod.url}
                refForVideoPlayer={playerRef}
                onReady={onReady}
                watchAnalyticsProperties={{
                  contentType: "matchVod",
                  identifier: vod.id,
                  source: "Match VOD",
                }}
                playAnalyticsProperties={{ source: "Match VOD" }}
                options={{
                  controls: true,
                  autoplay: true,
                  fluid: true,
                  poster: vod.thumbnail || undefined,
                }}
              />

              {flash && (
                <FlashOverlay key={flash.id}>
                  <FlashRound>Round {flash.round}</FlashRound>
                  <FlashTitle>{flash.text}</FlashTitle>
                </FlashOverlay>
              )}

              {markers.length > 0 && domainSec > 0 && (
                <Seeker
                  ref={barRef}
                  onMouseDown={(e) => {
                    setDragging(true);
                    seekFromClientX(e.clientX);
                  }}
                >
                  <Track>
                    <Fill style={{ width: `${playheadPct}%` }} />
                    {markers.map((m) =>
                      momentsByRound.get(m.round)?.length ? (
                        <MomentBand
                          key={m.round}
                          $best={m.round === bestRound}
                          style={{
                            left: `${seg(m).left}%`,
                            width: `${seg(m).width}%`,
                          }}
                        />
                      ) : null,
                    )}
                    {dividers.map((left, i) => (
                      <Divider key={i} style={{ left: `${left}%` }} />
                    ))}
                  </Track>
                  {markers.map((m) => (
                    <Segment
                      key={m.round}
                      style={{
                        left: `${seg(m).left}%`,
                        width: `${seg(m).width}%`,
                      }}
                      $hasMoments={!!momentsByRound.get(m.round)?.length}
                      onMouseEnter={() => setHoveredRound(m.round)}
                      onMouseLeave={() =>
                        setHoveredRound((r) => (r === m.round ? null : r))
                      }
                    />
                  ))}
                  <Playhead style={{ left: `${playheadPct}%` }} />
                  {hoveredMarker && (
                    <RoundTip
                      style={{
                        left: `${clamp(
                          seg(hoveredMarker).left +
                            seg(hoveredMarker).width / 2,
                          16,
                          84,
                        )}%`,
                      }}
                    >
                      {(momentsByRound.get(hoveredMarker.round) || []).length >
                      0 ? (
                        (momentsByRound.get(hoveredMarker.round) || [])
                          .slice(0, 3)
                          .map((mm, i) => (
                            <TipMoment key={mm.id}>
                              <TipTop>
                                {i === 0 && (
                                  <TipRound>
                                    Round {hoveredMarker.round}
                                  </TipRound>
                                )}
                                <TipTitle>{mm.title}</TipTitle>
                                {mm.id === bestMomentId && (
                                  <TipBest>Best Moment</TipBest>
                                )}
                              </TipTop>
                              <MomentSummary moment={mm} hideTitle />
                            </TipMoment>
                          ))
                      ) : (
                        <>
                          <TipRound>Round {hoveredMarker.round}</TipRound>
                          <TipEmpty>No highlights this round</TipEmpty>
                        </>
                      )}
                    </RoundTip>
                  )}
                </Seeker>
              )}
            </PlayerWrap>

            {markers.length > 0 && (
              <SeekerKey>
                <KeyItem>
                  <KeyDot $variant="best" />
                  Best Moment
                </KeyItem>
                <KeyItem>
                  <KeyDot $variant="highlight" />
                  Highlight round
                </KeyItem>
                <KeyHint>
                  Hover a round to preview, click anywhere to jump
                </KeyHint>
              </SeekerKey>
            )}
          </Stage>

          <Rail>
            <RailInner>
              <RailHeader>
                <RailTitle>Your Moments</RailTitle>
                {moments.length > 0 && <RailCount>{moments.length}</RailCount>}
              </RailHeader>
              <RailSub>
                Every highlight from this match. Click one to jump there, then
                1-Click Clip or Edit &amp; Clip it.
              </RailSub>
              <RailList onWheel={pauseAutoScroll} onTouchMove={pauseAutoScroll}>
                {loading && <RailEmpty>Loading moments...</RailEmpty>}
                {!loading && moments.length === 0 && (
                  <RailEmpty>No moments for this match.</RailEmpty>
                )}
                {moments.map((moment) => (
                  <MomentRow
                    key={moment.id}
                    moment={moment}
                    matchId={matchId}
                    autoScroll={autoScroll}
                    isBest={moment.id === bestMomentId}
                    active={
                      activeRound != null && momentRound(moment) === activeRound
                    }
                    onJump={() => jumpToMoment(moment)}
                  />
                ))}
              </RailList>
            </RailInner>
          </Rail>
        </Body>
      </Shell>
    </Backdrop>,
    document.body,
  );
};
